/**
 * Repairer capability controlled vocabulary — BP-37 Appendix C v1.0, "minimal v1 core"
 * (CUR-1102). Codes are stable and additive: retire, never reuse. Allocation (CUR-1105) reads
 * `allocationRole` to decide whether a missing capability makes a repairer ineligible
 * (`hard-filter`) or only lowers it (`rank`). Starred capabilities in the spec (`accredited: true`)
 * are only trusted while a compliance item backs them; that lifecycle layer is BP-37 S3 and not
 * modelled yet, so the flag is informational here.
 */

export const REPAIRER_CAPABILITY_FACETS = ['vehicle-class', 'work-type', 'construction', 'propulsion'] as const;

export type RepairerCapabilityFacet = (typeof REPAIRER_CAPABILITY_FACETS)[number];

export const REPAIRER_CAPABILITY_FACET_LABELS = {
    'vehicle-class': 'Vehicle class',
    'work-type': 'Work type',
    construction: 'Construction and materials',
    propulsion: 'Propulsion',
} as const satisfies Record<RepairerCapabilityFacet, string>;

export const REPAIRER_CAPABILITY_ALLOCATION_ROLES = ['hard-filter', 'rank'] as const;

export type RepairerCapabilityAllocationRole = (typeof REPAIRER_CAPABILITY_ALLOCATION_ROLES)[number];

export type RepairerCapabilityDefinition = {
    label: string;
    definition: string;
    facet: RepairerCapabilityFacet;
    allocationRole: RepairerCapabilityAllocationRole;
    /** Spec ★: only trusted while a backing compliance item is current (BP-37 S3, later). */
    accredited: boolean;
};

export const REPAIRER_CAPABILITIES = {
    // Facet A — vehicle class (all hard filters)
    'VC-LP': {
        label: 'Light passenger',
        definition: 'Sedan, hatch, wagon, SUV, people-mover up to 4.5t GVM',
        facet: 'vehicle-class',
        allocationRole: 'hard-filter',
        accredited: false,
    },
    'VC-LC': {
        label: 'Light commercial',
        definition: 'Ute, van, light truck up to 4.5t GVM',
        facet: 'vehicle-class',
        allocationRole: 'hard-filter',
        accredited: false,
    },
    'VC-HR': {
        label: 'Heavy rigid',
        definition: 'Rigid truck over 4.5t GVM',
        facet: 'vehicle-class',
        allocationRole: 'hard-filter',
        accredited: false,
    },
    'VC-HA': {
        label: 'Heavy articulated',
        definition: 'Prime mover, semi, B-double',
        facet: 'vehicle-class',
        allocationRole: 'hard-filter',
        accredited: false,
    },
    'VC-BUS': {
        label: 'Bus and coach',
        definition: 'PSV bodies',
        facet: 'vehicle-class',
        allocationRole: 'hard-filter',
        accredited: false,
    },
    'VC-TRL': {
        label: 'Trailer',
        definition: 'Semi, dog, tipper, box, curtainsider',
        facet: 'vehicle-class',
        allocationRole: 'hard-filter',
        accredited: false,
    },
    'VC-RV': {
        label: 'Caravan, motorhome and RV',
        definition: 'Towable and motorised recreational',
        facet: 'vehicle-class',
        allocationRole: 'hard-filter',
        accredited: false,
    },
    'VC-MC': {
        label: 'Motorcycle and scooter',
        definition: 'Motorcycles and scooters',
        facet: 'vehicle-class',
        allocationRole: 'hard-filter',
        accredited: false,
    },
    'VC-PLANT': {
        label: 'Mobile plant and equipment',
        definition: 'Earthmoving, forklift, agricultural and mobile plant',
        facet: 'vehicle-class',
        allocationRole: 'hard-filter',
        accredited: false,
    },
    // Facet B — work type (minimal core subset)
    'WT-STRUCT': {
        label: 'Structural repair',
        definition: 'Chassis or monocoque realignment on jig or bench; structural welding',
        facet: 'work-type',
        allocationRole: 'hard-filter',
        accredited: true,
    },
    'WT-PANEL': {
        label: 'Non-structural panel',
        definition: 'Bolt-on and cosmetic panel repair or replacement',
        facet: 'work-type',
        allocationRole: 'rank',
        accredited: false,
    },
    'WT-PAINT': {
        label: 'Paint and refinish',
        definition: 'Spray booth refinishing',
        facet: 'work-type',
        allocationRole: 'hard-filter',
        accredited: false,
    },
    'WT-PDR': {
        label: 'Paintless dent and hail',
        definition: 'PDR and catastrophe hail',
        facet: 'work-type',
        allocationRole: 'hard-filter',
        accredited: false,
    },
    'WT-GLASS': {
        label: 'Automotive glass',
        definition: 'Windscreen, side and rear glass',
        facet: 'work-type',
        allocationRole: 'hard-filter',
        accredited: true,
    },
    'WT-ADAS': {
        label: 'ADAS calibration',
        definition: 'Static and/or dynamic calibration',
        facet: 'work-type',
        allocationRole: 'hard-filter',
        accredited: true,
    },
    'WT-HCHASSIS': {
        label: 'Heavy chassis and cab',
        definition: 'Heavy chassis straightening, cab-off or replacement',
        facet: 'work-type',
        allocationRole: 'hard-filter',
        accredited: true,
    },
    // Facet C — construction and materials (minimal core subset)
    'CM-ALU': {
        label: 'Aluminium-capable',
        definition: 'Isolated aluminium bay and dedicated tooling',
        facet: 'construction',
        allocationRole: 'hard-filter',
        accredited: true,
    },
    // Facet D — propulsion (minimal core subset)
    'PV-HYB': {
        label: 'Hybrid and PHEV HV-safe',
        definition: 'High-voltage isolation for hybrids',
        facet: 'propulsion',
        allocationRole: 'hard-filter',
        accredited: true,
    },
    'PV-EV': {
        label: 'EV and BEV certified',
        definition: 'High-voltage certified, EV-safe bay',
        facet: 'propulsion',
        allocationRole: 'hard-filter',
        accredited: true,
    },
} as const satisfies Record<string, RepairerCapabilityDefinition>;

export type RepairerCapabilityCode = keyof typeof REPAIRER_CAPABILITIES;

/** Ordered as the spec lists them: vehicle class, work type, construction, propulsion. */
export const REPAIRER_CAPABILITY_CODES = Object.keys(REPAIRER_CAPABILITIES) as [
    RepairerCapabilityCode,
    ...RepairerCapabilityCode[],
];

export function isRepairerCapabilityCode(value: string): value is RepairerCapabilityCode {
    return Object.hasOwn(REPAIRER_CAPABILITIES, value);
}

/**
 * Claimed baseline for the 141 Clarion primary repairers (CUR-1102 decision 1): every one is an
 * iBodyshop smash repairer, so light vehicles plus panel and paint. Without a baseline the
 * Appendix C hard filters would make every seeded repairer ineligible on day one.
 */
export const REPAIRER_SEED_BASELINE_CAPABILITIES = [
    'VC-LP',
    'VC-LC',
    'WT-PANEL',
    'WT-PAINT',
] as const satisfies readonly RepairerCapabilityCode[];

/** Upper bound for `maxGvmKg`; B-doubles and road trains sit well under this. */
export const MAX_GVM_KG_LIMIT = 200_000;
